<script setup lang="ts">
import { computed, ref } from 'vue'

import { contentfulUrl } from '@internal/format-contentful'
import Button from '@satch-ui/components/button/Button.vue'
import CircleBadge from '@storefront-satch/components/atoms/circle-badge/CircleBadge.vue'
import TeaserCardItemContent from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemContent.vue'
import TeaserCardItemMedia from '@storefront-satch/components/molecules/teaser-card/TeaserCardItemMedia.vue'
import type {
  TeaserCardItemAdditionalProps,
  TeaserCardItemProps,
} from '@storefront-satch/components/molecules/teaser-card/types'
import { clsx } from 'clsx'

import { ArrowRightIcon, PlayIcon } from '@fond-of/satch-icons'

const props = defineProps<TeaserCardItemProps & TeaserCardItemAdditionalProps>()

const isVideoPlaying = ref<boolean>(false)

const hasVideo = computed(
  () =>
    !!(
      props.media?.videoMobile?.url ||
      props.media?.videoTablet?.url ||
      props.media?.videoDesktop?.url
    )
)
const videoHasControls = computed(() => props.media?.videoControls)
const videoIsAutoplay = computed(() => props.media?.videoAutoplay)
const hasLinkAndLinkText = computed(() => !!(props.link && props.linkText))

const width = computed(() => props.width ?? 'single')
const type = computed(() => props.type ?? 'half')

const isFullVideo = computed(() => {
  return hasVideo.value && type.value === 'full'
})

const showVideoWithLinkButton = computed(() => {
  if (
    isFullVideo.value &&
    !videoHasControls.value &&
    hasLinkAndLinkText.value
  ) {
    return true
  }

  return type.value === 'half' && hasLinkAndLinkText.value && hasVideo.value
})

const showVideoWithPlayButton = computed(
  () => isFullVideo.value && !videoIsAutoplay.value && !videoHasControls.value
)

const isLinkElement = computed(() => {
  return props.link && !showVideoWithLinkButton.value
})
</script>

<template>
  <div
    :id="props.sys.id"
    :class="[
      'relative',
      {
        // grid
        'mb-12.5 w-full sm:mb-0': props.groupType === 'grid',
        'col-end-[span_2]': props.groupType === 'grid' && width === 'double',

        // slider
        'inline-block w-full py-0 sm:block sm:p-0':
          props.groupType === 'slider',
      },
    ]"
    :data-cfid="props.sys.id"
    data-label="Teaser Card Item"
    :data-title="props.internalTitle"
  >
    <component
      :is="isLinkElement ? 'a' : 'div'"
      class="relative flex flex-wrap content-start p-0 hover:no-underline"
      :href="contentfulUrl(props.link)"
      :data-gtm-attr="
        isLinkElement ? 'link_teaser-card-item-medium' : undefined
      "
      :data-gtm-label="isLinkElement ? props.internalTitle : undefined"
      :aria-label="
        isLinkElement
          ? props.linkAriaLabel?.trim() || props.linkText?.trim() || undefined
          : undefined
      "
    >
      <span
        v-if="props.showNumber"
        class="absolute left-4 top-4 z-10 md:left-[25px] md:top-[25px]"
      >
        <div class="size-8 lg:size-12">
          <CircleBadge>
            {{ props.number }}
          </CircleBadge>
        </div>
      </span>

      <TeaserCardItemMedia
        v-if="props.media"
        :media="props.media"
        :size="props.size"
        :type="type"
        :width="width"
        :group-type="props.groupType"
        :media-aspect-ratio="props.mediaAspectRatio"
        :media-fit="props.mediaFit"
        @update:video-playing="
          (state) => {
            isVideoPlaying = state
          }
        "
      />

      <TeaserCardItemContent
        :hide-content="isVideoPlaying && !videoIsAutoplay"
        :is-clickable="hasLinkAndLinkText && !isVideoPlaying"
        :headline="props.content?.headline"
        headline-size="medium"
        :headline-level="props.content?.headlineLevel || null"
        :text="props.content?.text"
        :content-style="
          props.contentStyle || (type === 'full' ? 'white' : 'black')
        "
        :link-style="props.linkStyle"
        :text-size="type === 'full' ? 'base' : 'small'"
        :is-text-compact="props.groupType === 'slider'"
        :class="[
          clsx('md:order-0 order-10 m-0 flex flex-col flex-wrap', {
            'px-4 lg:px-5': type === 'half',
            'pb-7.5 absolute bottom-0 left-0 top-0 mb-0 flex flex-col justify-end pl-5 pr-5 pt-2.5 text-white md:px-5 lg:py-10 lg:pb-5 lg:pl-5 lg:pr-5 lg:pt-5':
              type === 'full',
            'w-full md:w-[65%]': width === 'double',
            'w-full': width !== 'double',
          }),
        ]"
      >
        <template #contentFooter>
          <Button
            v-if="
              (hasLinkAndLinkText && !hasVideo) ||
              showVideoWithLinkButton ||
              showVideoWithPlayButton
            "
            :as="showVideoWithLinkButton ? 'a' : 'span'"
            :href="
              showVideoWithLinkButton ? contentfulUrl(props.link) : undefined
            "
            class="mt-2.5 w-fit"
            :variant="props.linkStyle || 'outlineWhite'"
            :hover="false"
          >
            {{ props.linkText }}
            <PlayIcon
              v-if="showVideoWithLinkButton"
              class="size-4"
            />
            <ArrowRightIcon
              v-else
              class="size-4 stroke-2"
            />
          </Button>
        </template>
      </TeaserCardItemContent>
    </component>
  </div>
</template>
